"use client";

import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import type { Editor } from "@tiptap/react";
import { Plus, Trash2 } from "lucide-react";

import { Input } from "@/components/ui/input";
import { t } from "@/lib/localization";
import {
    WalkthroughButton,
    WalkthroughInfoStep,
    WalkthroughStep,
} from "@/lib/utils/lomdaV3/types";
import { headingFieldProps } from "../../../lib/ownFonts";
import RichText from "../text/richText";
import RichTextToolbar from "../text/richTextToolbar";
import StepLinkSelect from "./stepLinkSelect";

interface Props {
    step: WalkthroughInfoStep;
    steps: WalkthroughStep[];
    onUpdate: (patch: Partial<WalkthroughInfoStep>) => void;
}

export default function InfoStepEditor({ step, steps, onUpdate }: Props) {
    const [activeEditor, setActiveEditor] = useState<Editor | null>(null);

    const updateButton = (id: string, patch: Partial<WalkthroughButton>) =>
        onUpdate({
            buttons: step.buttons.map((b) =>
                b.id === id ? { ...b, ...patch } : b
            ),
        });

    const addButton = () =>
        onUpdate({
            buttons: [
                ...step.buttons,
                { id: uuidv4(), text: "", linkStepId: "" },
            ],
        });

    const removeButton = (id: string) =>
        onUpdate({ buttons: step.buttons.filter((b) => b.id !== id) });

    return (
        <div className="relative mx-auto flex max-w-2xl flex-col items-center gap-6 rounded-2xl border bg-card p-10 text-center">
            {activeEditor && <RichTextToolbar editor={activeEditor} />}

            <div className="w-full">
                <RichText
                    {...headingFieldProps(step, onUpdate, "text-center")}
                    placeholder={t("lomda-v3-editor.text-variant.heading-placeholder")}
                    onFocusEditor={setActiveEditor}
                />
            </div>
            <div className="w-full text-muted-foreground">
                <RichText
                    initialHtml={step.text}
                    placeholder={t("lomda-v3-editor.block.text.placeholder")}
                    onFocusEditor={setActiveEditor}
                    onCommit={(text) => onUpdate({ text })}
                />
            </div>

            <div className="flex w-full flex-col gap-2">
                {step.buttons.map((b) => (
                    <div
                        key={b.id}
                        className="flex items-center gap-2 rounded-lg border bg-muted/20 p-2"
                    >
                        <Input
                            value={b.text}
                            onChange={(e) => updateButton(b.id, { text: e.target.value })}
                            placeholder={t("lomda-v3-editor.walkthrough.button-text")}
                            className="lomda-v3-page-text h-8 flex-1"
                        />
                        <StepLinkSelect
                            steps={steps}
                            currentStepId={step.id}
                            value={b.linkStepId}
                            onChange={(linkStepId) => updateButton(b.id, { linkStepId })}
                        />
                        <button
                            type="button"
                            onClick={() => removeButton(b.id)}
                            className="flex size-8 shrink-0 items-center justify-center rounded text-muted-foreground transition hover:text-destructive"
                        >
                            <Trash2 className="size-4" />
                        </button>
                    </div>
                ))}
                <button
                    type="button"
                    onClick={addButton}
                    className="flex items-center justify-center gap-1.5 rounded-lg border border-dashed py-2 lomda-v3-text-small text-muted-foreground transition hover:border-primary hover:text-primary"
                >
                    <Plus className="size-4" />
                    {t("lomda-v3-editor.walkthrough.add-button")}
                </button>
            </div>
        </div>
    );
}
